import { inject, Injectable } from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { Store } from '@ngrx/store';
import { getAI, getGenerativeModel, VertexAIBackend, ChatSession, SchemaType, Tool } from 'firebase/ai';
import { getApp } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { AiEvaluationResult, AiResponse } from '../models/ai.model';
import { QuizQuestion } from '../models/question.quiz.model';
import { AppState } from '../state/appstate';

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private functions = inject(Functions);
  store = inject(Store<AppState>);
  private logger: NGXLogger = inject(NGXLogger);

  startQuizChat(history: any[] = []): ChatSession {
    const app = getApp();
    const backend = new VertexAIBackend();
    const ai = getAI(app, { backend });

    const systemInstruction = `You are a Bible Quiz Question Generator. Your ONLY purpose is to help the user generate, edit, and refine Bible quiz questions.
    
CRITICAL RULES:
1. STRICT BOUNDARY: You must ONLY assist with Bible quiz questions. If the user asks about ANY other topic (e.g. general knowledge, baking, programming, sports, history unrelated to the Bible, etc.), you must politely but firmly reject their request and remind them that you are only allowed to generate and refine Bible quiz questions.
2. BIBLE REFERENCE/TOPIC REQUIRED: You must always have a specific Bible reference (e.g., John 3, Hebrews 11) or a specific Biblical topic/theme (e.g., faith, grace, creation) to generate questions. If the user hasn't provided one yet, or if they ask a vague question, you MUST ask them to specify a reference or topic.
3. MAX 10 QUESTIONS: You can generate up to a maximum of 10 questions at a time. If the user requests more, limit it to 10 and inform them.
4. RESPONSE FORMAT: You must return a JSON response adhering to the response schema. The JSON must contain:
   - "chatResponse": A friendly, helpful conversational reply to the user. Explain what questions you have generated/updated or ask clarifying questions.
   - "questions": An array of quiz questions. If you are generating new questions or updating existing questions based on user request, return the full list of active questions (up to 10) in this array. If no questions are generated or modified, keep this array empty.
5. QUESTION STRUCTURE:
   - Suggest up to 10 search tags for each question in the "tags" array (e.g., ["faith", "hope", "love", "hebrews11"]). Suggest relevant, descriptive tags.
   - For "Multiple Choice" type, provide 3 to 4 options and set "correctOptionId".
   - For "True/False" type, options must be exactly [ {"id": "A", "text": "True"}, {"id": "B", "text": "False"} ] and correctOptionId set, with simpleAnswer as "True" or "False".
   - For "Fill In The Blank" and "Short Answer", simpleAnswer is required and options should be empty or omitted.
   - Assign reasonable "difficulty" (1 to 10) and "score" (1 to 10).
6. EDITING & REFINING: If the user asks to modify the current questions (e.g., "make question 2 harder", "change the true/false question to multiple choice", "add a tag 'grace' to all questions"), you must modify the active list of questions provided by the user in the conversation history and return the updated array in "questions".
7. SUBMITTING/CREATING QUESTIONS: If the user explicitly asks to save, create, persist, or submit the prepared questions to the database or backend, you MUST set the "submitQuestions" property to true. Do NOT claim you have saved or created the questions unless you have set "submitQuestions" to true.
8. DUPLICATE AVOIDANCE: Before generating any new questions, you MUST call the "searchExistingQuestions" tool to search for pre-existing questions in the database for the given Bible reference (book, chapter, and optionally verse range). Compare the user's request with the returned list of existing questions. Do not generate questions that are identical or highly similar to those already in the database.`;

    const responseSchema = {
      type: 'object',
      properties: {
        chatResponse: {
          type: 'string',
          description: 'The response message back to the user, explaining what was done or requesting clarification.'
        },
        submitQuestions: {
          type: 'boolean',
          description: 'Set this to true ONLY when the user explicitly requests to save, create, persist, or submit the prepared questions to the database/backend.'
        },
        questions: {
          type: 'array',
          description: 'The active set of quiz questions. Omit or leave empty if no questions were generated or modified in this turn.',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'Unique identifier (preserve existing if modifying, or generate new random string for new questions).' },
              questionText: { type: 'string', description: 'The text of the question.' },
              difficulty: { type: 'number', description: 'The difficulty level from 1 (easy) to 10 (hard).' },
              score: { type: 'number', description: 'The score value from 1 to 10.' },
              explanation: { type: 'string', description: 'Explanation of the correct answer.' },
              questionType: {
                type: 'string',
                enum: ['Multiple Choice', 'Fill In The Blank', 'True/False', 'Short Answer'],
                description: 'The type of question.'
              },
              options: {
                type: 'array',
                description: 'List of options. Required for Multiple Choice and True/False. For True/False, must be exactly [ {id: "A", text: "True"}, {id: "B", text: "False"} ].',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    text: { type: 'string' }
                  },
                  required: ['id', 'text']
                }
              },
              correctOptionId: { type: 'string', description: 'The id of the correct option (required for Multiple Choice and True/False).' },
              simpleAnswer: { type: 'string', description: 'The simple/correct answer text (required for Fill In The Blank, True/False, and Short Answer).' },
              reference: {
                type: 'object',
                properties: {
                  book: { type: 'string', description: 'Name of the Bible book.' },
                  chapter: { type: 'number', description: 'Chapter number.' },
                  verse: { type: 'number', description: 'Verse number (optional).' }
                },
                required: ['book', 'chapter']
              },
              tags: {
                type: 'array',
                description: 'Suggested search tags for this question (up to 10 tags).',
                items: { type: 'string' }
              }
            },
            required: ['id', 'questionText', 'difficulty', 'score', 'questionType', 'reference']
          }
        }
      },
      required: ['chatResponse']
    };

    const tools: Tool[] = [
      {
        functionDeclarations: [
          {
            name: 'searchExistingQuestions',
            description: 'Search for existing quiz questions in the database question bank by Bible reference to prevent duplicates.',
            parameters: {
              type: SchemaType.OBJECT as const,
              properties: {
                book: {
                  type: SchemaType.STRING,
                  description: 'The name of the Bible book (e.g., "Genesis", "John").'
                },
                chapter: {
                  type: SchemaType.NUMBER,
                  description: 'The chapter number.'
                },
                fromVerse: {
                  type: SchemaType.NUMBER,
                  description: 'Optional starting verse number of the range.'
                },
                toVerse: {
                  type: SchemaType.NUMBER,
                  description: 'Optional ending verse number of the range.'
                }
              },
              required: ['book', 'chapter']
            }
          }
        ]
      }
    ];

    const model = getGenerativeModel(ai, {
      model: 'gemini-2.5-flash',
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: responseSchema as any,
      },
      systemInstruction,
      tools,
    });

    return model.startChat({ history });
  }

  async generateQuestions(
    groupId: string,
    book: string,
    chapter: number,
    verse?: number,
    count: number = 3,
  ): Promise<QuizQuestion[]> {
    const getQuiz = httpsCallable(this.functions, 'quiz_generateQuestions', {
      limitedUseAppCheckTokens: true,
    });

    const user = await firstValueFrom(
      this.store.select((state) => state.user.user),
    );

    const result = await getQuiz({
      groupId,
      reference: { book, chapter, verse },
      count,
      createdById: user?.id,
      createdByName: user?.name,
      createdByPhotoUrl: user?.photoUrl,
    });

    return result.data as QuizQuestion[];
  }

  async evaluateWithAI(
    userAnswer: string,
    question: QuizQuestion,
    shortAnswerPercentage: number = 50,
  ): Promise<AiEvaluationResult> {
    this.logger.debug(
      `evaluateWithAI :: Called for question: "${question.questionText}". User answer: "${userAnswer}". Reference: ${question.reference?.book} ${question.reference?.chapter}:${question.reference?.verse}`,
    );
    const hasReference = !!(
      question.reference &&
      question.reference.book &&
      question.reference.book.trim()
    );
    // Format the Bible reference string if it exists (handling undefined chapter/verse)
    let referenceText = '';
    if (hasReference) {
      const ref = question.reference;
      referenceText = ref.book;
      if (ref.chapter !== undefined && ref.chapter !== null) {
        referenceText += ` ${ref.chapter}`;
        if (ref.verse !== undefined && ref.verse !== null) {
          referenceText += `:${ref.verse}`;
        }
      }
    }
    // 2. Adapt the grading instructions dynamically
    const systemInstruction = hasReference
      ? `You are an objective Bible quiz grader. Check the user's answer against the question, the expected answer, and the Biblical context of the reference passage. Be understanding of minor spelling mistakes, typos, or synonyms, but ensure at least ${shortAnswerPercentage}% of the core meaning matches the context.`
      : `You are an objective Bible quiz grader. Check the user's answer against the question and the expected answer. Be understanding of minor spelling mistakes, typos, or synonyms, but ensure at least ${shortAnswerPercentage}% of the core meaning matches.`;

    // 3. Initialize the AI instance and model with structured JSON schema
    const app = getApp();
    const backend = new VertexAIBackend(); // Use production-ready Vertex AI (requires Blaze plan and App Check debug token in dev)

    const ai = getAI(app, { backend });

    const model = getGenerativeModel(ai, {
      model: 'gemini-2.5-flash',
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: 'object',
          properties: {
            result: {
              type: 'boolean',
              description:
                `True if the userAnswer matches at least ${shortAnswerPercentage}% of the meaning of the correct answer (and the bible passage context if provided). Otherwise false.`,
            },
            explanation: {
              type: 'string',
              description:
                'A brief 1-2 sentence explanation of why the answer is correct or incorrect.',
            },
          },
          required: ['result', 'explanation'],
        },
      } as any, // <-- Cast as any here to bypass TypeScript compilation error
      systemInstruction,
    });

    // 4. Construct the prompt dynamically
    let prompt = `Evaluate the user's answer to this quiz question:
    
    - Question: "${question.questionText}"
    - Expected Correct Answer: "${question.simpleAnswer || 'N/A'}"`;
    if (hasReference) {
      prompt += `\n    - Bible Passage Reference: ${referenceText}`;
    }
    prompt += `\n    - User's Answer: "${userAnswer}"
    
    Provide your response in the requested JSON format.`;
    try {
      this.logger.debug('Sending prompt to AI model:', {
        prompt,
        systemInstruction,
      });
      const response = await model.generateContent(prompt);
      const textResponse = response.response.text();

      if (!textResponse) {
        throw new Error('Empty response received from AI model.');
      } else {
        this.logger.debug(
          'Received response from AI model:',
          JSON.stringify(textResponse),
        );
      }

      this.logger.debug('Raw response from AI model:', { textResponse });

      const parsed = JSON.parse(textResponse);
      const usageMetadata = response.response.usageMetadata;
      if (usageMetadata) {
        this.logger.debug('AI model token usage:', usageMetadata);
        parsed.usage = {
          promptTokens: usageMetadata.promptTokenCount ?? 0,
          completionTokens: usageMetadata.candidatesTokenCount ?? 0,
          totalTokens: usageMetadata.totalTokenCount ?? 0,
        };
      }
      return parsed;
    } catch (error: any) {
      this.logger.error(
        `Failed to evaluate quiz answer via Firebase AI: ${error?.message || error}, Stack: ${error?.stack || ''}`,
      );

      // Local fallback evaluation
      const correctAnswer = question.simpleAnswer?.toLowerCase().trim();
      const userAns = userAnswer.toLowerCase().trim();
      const isCorrect = !!(correctAnswer && userAns === correctAnswer);

      return {
        result: isCorrect,
        explanation: isCorrect
          ? 'Correct!'
          : `We had trouble reaching the AI. The reference answer is: ${question.simpleAnswer || 'not specified'}`,
      };
    }
  }

  async extractReceipt(params: {
    groupId: string;
    gcsPath: string;
    mimeType: string;
  }): Promise<AiResponse<{ amount: number; currency: string; date: string; description: string; from?: string; to?: string; tags?: string[] }>> {
    const fn = httpsCallable(this.functions, 'accounting_extractReceipt', {
      limitedUseAppCheckTokens: true,
    });
    this.logger.debug('ai.service.ts :: Calling accounting_extractReceipt Cloud Function with params:', params);
    
    try {
      const result = await fn(params);
      this.logger.info('ai.service.ts :: Received response from accounting_extractReceipt Cloud Function:', JSON.stringify(result.data));
      return result.data as AiResponse<{ amount: number; currency: string; date: string; description: string; from?: string; to?: string; tags?: string[] }>;
    } catch (error) {
      this.logger.error('ai.service.ts :: Error calling accounting_extractReceipt:', error);
      throw error;
    }
  }
}
